/**
 * Promote a seeded (not-yet-on-the-server) context to a real one, in place.
 *
 * bootstrapContextView seeds an empty editable graph when the owner opens a
 * URL for a context that doesn't exist yet. The first statement add creates
 * the context server-side and returns the recalculated graph, which the
 * caller applies through the normal post-save flow. What that flow can't
 * provide is everything keyed on the context *record*: the drawer list, the
 * resolved context type, and the legacy `userFactory` context list that
 * analytics derives the context id from. This refreshes those without a
 * page reload, so a running voice-dictation session keeps its microphone.
 *
 * Never rejects: the statement is already saved and the graph is live, so a
 * failed refresh only leaves the drawer row behind until the next page load.
 */

import { useZustand } from '../../hooks/useZustand'
import { fetchOwnerContexts } from '../../hooks/useApi'
import { useContextsStore, findContextByName } from '../useContextsStore'
import { resolveContextType } from './bootstrapContextView'

const RETRY_DELAY_MS = 1500

let inFlight = null

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const refreshContextRecords = async graphOwnerUsername => {
    const response = await fetchOwnerContexts(graphOwnerUsername)
    if (response.accessDenied || !Array.isArray(response.contextRecords)) {
        return false
    }
    useContextsStore.getState().setContexts(response.contextRecords)
    return true
}

// Legacy EJS code (ContextMenu.ejs) hands userFactory tuples in the order
// [name, type, id, createdAt, isFavorite, defaultRevisionUrl]; index 2 is
// what getContextIDByName and Analytics.jsx read.
const syncLegacyContextList = records => {
    const userFactory = window.userFactory
    if (!userFactory?.setAllContexts) return
    userFactory.setAllContexts(
        records.map(record => [
            record.name,
            (record.type || '').toLowerCase(),
            record.id,
            record.createdAt,
            !!record.isFavorite,
            record.defaultRevisionUrl ?? '',
        ])
    )
}

const promote = async ({ contextName, graphOwnerUsername }) => {
    // The save that precedes this call already flushed the server-side
    // contexts cache, so the first fetch normally has the new record; one
    // retry covers a replica lagging behind.
    let record = null
    for (let attempt = 0; attempt < 2 && !record; attempt++) {
        if (attempt > 0) await delay(RETRY_DELAY_MS)
        if (!(await refreshContextRecords(graphOwnerUsername))) return
        record = findContextByName(useContextsStore.getState(), contextName)
    }

    syncLegacyContextList(useContextsStore.getState().contexts)

    if (!record) {
        console.warn(
            `[promoteSeededContext] "${contextName}" missing from the ` +
                'refreshed contexts list; drawer will catch up on next load'
        )
        return
    }

    useZustand
        .getState()
        .setCurrentContextType(resolveContextType(contextName, record.type))
    window.userFactory?.setContextOptions?.('type', record.type)
}

export const promoteSeededContext = ({ contextName, graphOwnerUsername }) => {
    if (inFlight) return inFlight
    inFlight = promote({ contextName, graphOwnerUsername })
        .catch(err => {
            console.warn('[promoteSeededContext] failed:', err)
        })
        .finally(() => {
            inFlight = null
        })
    return inFlight
}
